import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { PROJECT_STATUS_LABEL } from "@lifeat/shared/projects/ProjectStatus";
import { Project } from "@lifeat/shared/projects/ProjectsTypes";
import { TaskItemType } from "@lifeat/shared/task-management/TaskManagementTypes";
import { formatMinutesToString, getDateFromISODateString, getDisplayDateString } from "@lifeat/shared/util/DateUtil";
import { DropdownMenuComponents } from "@common/LifeAtDropdownMenuV2/DropdownMenuComponents";
import { LifeAtBodyPrimary } from "@common/LifeAtText/LifeAtText";
import LoadingSpinner from "@common/LoadingSpinner/LoadingSpinner";
import { useToastListContext } from "@contexts/ToastListContext";
import Arrow from "@icons/Arrow-Left-Icon.svg";
import MoreActionsIcon from "@icons/Menu-TripleVerticalDots-Icon.svg";
import { FontSize } from "@themes/Fonts";
import Styles from "@themes/Styles";
import ProjectChip from "../../task-management/components/ProjectChip";
import { DropdownItem } from "../../task-management/components/TaskTagDropdownComponents";
import { DELETE_PROJECT_CONFIRMATION } from "../ProjectConstants";
import { tasksByProjectId$ } from "../ProjectGlobals";
import { ProjectsService } from "../ProjectsService";
import ProjectsTaskProgressBar from "./ProjectsTaskProgressBar";

const ProjectItemContainer = styled.div<{ isDisabled: boolean }>`
    display: flex;
    flex-direction: column;
    padding: 15px;
    background-color: ${(props) => props.theme.backgroundPrimary};
    border-radius: ${Styles.borderRadiusSharp};
    border: 1px solid ${(props) => props.theme.borderPrimary};
    width: 235px;
    gap: 10px;
    cursor: ${(props) => (props.isDisabled ? "not-allowed" : "pointer")};
    opacity: ${(props) => (props.isDisabled ? "0.7" : "1")};
`;

const ProjectTitleRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const StyledMoreActionsIcon = styled(MoreActionsIcon)`
    fill: ${(props) => props.theme.iconColor};
    cursor: pointer;
    transform: rotate(90deg);
`;

const StyledDropdownButton = styled(DropdownMenuComponents.Trigger)`
    padding: 1px 8px;
`;

const StyledProjectsTaskProgressBar = styled(ProjectsTaskProgressBar)`
    padding: 0;
    > div {
        height: 6px !important;
    }
`;

const ArrowIcon = styled(Arrow)`
    fill: ${(props) => props.theme.textColorPrimary};
    scale: 0.8;
    transform: rotate(180deg);
`;

const StyledProjectChip = styled(ProjectChip)`
    padding: unset;
`;

const ProjectTimelineRow = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
`;

const EDIT_LABEL = "Edit";
const DELETE_LABEL = "Delete";

interface ProjectItemProps {
    readonly project: Project;
    readonly projectId: string;
    readonly setSelectedProjectId: (projectId: string | undefined) => void;
}
export default function ProjectItem(props: ProjectItemProps) {
    const { addToastNotification } = useToastListContext();
    const { projectId, project } = { ...props };
    const [isLoading, setIsLoading] = useState(false);

    const handleDeleteProject = (projectId: string) => {
        if (confirm(DELETE_PROJECT_CONFIRMATION)) {
            setIsLoading(true);
            ProjectsService.deleteProject(projectId).catch((error) => {
                addToastNotification("Failed to delete project, please try again", error.message);
            });
        }
    };

    const moreActionsMenu = (
        <DropdownMenuComponents.Root>
            <StyledDropdownButton onClick={(e) => e.stopPropagation()}>
                <StyledMoreActionsIcon />
            </StyledDropdownButton>
            <DropdownMenuComponents.Content>
                <DropdownItem onClick={() => props.setSelectedProjectId(projectId)}>{EDIT_LABEL}</DropdownItem>
                <DropdownItem onClick={() => handleDeleteProject(projectId)}>{DELETE_LABEL}</DropdownItem>
            </DropdownMenuComponents.Content>
        </DropdownMenuComponents.Root>
    );

    const taskList = Object.values(tasksByProjectId$.value[projectId] ?? {});

    const timeSpentOnProjectString = useMemo(() => {
        const timeSpentInMinutes = taskList.reduce(
            (total: number, task: TaskItemType) => total + (task.timeSpentMins ?? 0),
            0,
        );
        return `${formatMinutesToString(timeSpentInMinutes)} total`;
    }, [taskList]);

    return (
        <ProjectItemContainer
            isDisabled={isLoading}
            onClick={() => !isLoading && props.setSelectedProjectId(projectId)}
        >
            <ProjectTitleRow>
                <StyledProjectChip project={project} fontSizeCss={FontSize.normal} />
                {isLoading ? <LoadingSpinner size={17} /> : moreActionsMenu}
            </ProjectTitleRow>
            <LifeAtBodyPrimary>{PROJECT_STATUS_LABEL[project.status]}</LifeAtBodyPrimary>
            <LifeAtBodyPrimary>{timeSpentOnProjectString}</LifeAtBodyPrimary>
            <ProjectTimelineRow>
                {project.startDate && project.endDate && (
                    <>
                        <LifeAtBodyPrimary>
                            {getDisplayDateString(getDateFromISODateString(project.startDate))}
                        </LifeAtBodyPrimary>
                        <ArrowIcon />
                    </>
                )}
                {project.endDate && (
                    <LifeAtBodyPrimary>
                        {getDisplayDateString(getDateFromISODateString(project.endDate))}
                    </LifeAtBodyPrimary>
                )}
            </ProjectTimelineRow>
            <StyledProjectsTaskProgressBar projectColor={project.color} taskList={tasksByProjectId$.value[projectId]} />
        </ProjectItemContainer>
    );
}
